import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sparkles, UploadCloud, Loader2, Camera, Shirt, RefreshCw, Download, Share2, X, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function TryOn() {
  const { user, setAuthModalOpen, formatPrice, addToCart } = useApp();
  const [params] = useSearchParams();
  const [photo, setPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [looks, setLooks] = useState([]);

  const loadLooks = () => api.get("/tryon/looks").then(({ data }) => setLooks(data)).catch(() => {});

  useEffect(() => {
    if (!user) return;
    api.get("/tryon/photo").then(({ data }) => setPhoto(data.url ? abs(data.url) : null)).catch(() => {});
    loadLooks();
    api.get("/products").then(({ data }) => {
      setProducts(data);
      const pid = params.get("product");
      if (pid) {
        const p = data.find((x) => x.id === pid);
        if (p) setSelectedItems([p]);
      }
    });
  }, [user]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tryon/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(abs(data.url));
      setResult(null);
      toast.success("Photo saved — build your look");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleItem = (p) => {
    setSelectedItems((prev) => {
      if (prev.find((x) => x.id === p.id)) return prev.filter((x) => x.id !== p.id);
      if (prev.length >= 2) { toast.info("You can style up to 2 items (e.g. a top + bottom)"); return prev; }
      return [...prev, p];
    });
  };

  const runTryOn = async () => {
    if (!photo) { toast.error("Upload your photo first"); return; }
    if (!selectedItems.length) { toast.error("Select an item to try on"); return; }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await api.post("/tryon", { product_ids: selectedItems.map((p) => p.id) });
      setResult(abs(data.url));
      loadLooks();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try-on failed, please try again");
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = "bull-creations-look.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objUrl);
    } catch {
      toast.error("Could not download image");
    }
  };

  const shareImage = async (url) => {
    const shareText = "Check out my Bull Creations look! 🐂👕";
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], "bull-creations-look.png", { type: blob.type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Bull Creations look", text: shareText });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: "My Bull Creations look", text: shareText, url });
        return;
      }
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + url)}`, "_blank");
    } catch {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + url)}`, "_blank");
    }
  };

  const deleteLook = async (id) => {
    try {
      await api.delete(`/tryon/looks/${id}`);
      setLooks((prev) => prev.filter((l) => l.id !== id));
    } catch {
      toast.error("Could not delete");
    }
  };

  const addItemsToBag = (ids) => {
    const items = (ids || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
    if (!items.length) { toast.error("These items are no longer available"); return; }
    items.forEach((p) => addToCart(p, p.sizes?.[0] || "M", p.colors?.[0] || "", 1, true));
    toast.success(`${items.length} item${items.length > 1 ? "s" : ""} added to your bag`);
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()));
  const isSelected = (id) => selectedItems.some((x) => x.id === id);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Sparkles size={56} className="mx-auto text-[#E63946] mb-4" />
        <h1 className="font-display text-2xl font-bold mb-2">Virtual Try-On Studio</h1>
        <p className="text-slate-500 mb-6">Log in to upload your photo and see how our styles look on you.</p>
        <Button onClick={() => setAuthModalOpen(true)} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="tryon-login">Login to start</Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tryon-page">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D3557] via-[#17284a] to-[#0c1830] text-white p-8 mb-6">
        <div className="absolute -top-16 right-0 w-64 h-64 rounded-full bg-[#E63946]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-2"><Sparkles size={16} /> AI Virtual Try-On</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">See it on you</h1>
          <p className="text-slate-300 mt-2 max-w-2xl text-sm sm:text-base">Upload a clear, front-facing photo and pick any Bull Creations piece to preview how it looks on you. Results are an AI-generated approximation.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* photo */}
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Camera size={18} className="text-[#E63946]" /> Your Photo</h3>
          <div className="aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3" data-testid="tryon-photo-preview">
            {photo ? <img src={photo} alt="you" className="w-full h-full object-cover" /> : <Camera size={40} className="text-slate-300" />}
          </div>
          <input id="tryon-file" type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={uploadPhoto} data-testid="tryon-photo-input" />
          <Button variant="outline" className="w-full" disabled={uploading} onClick={() => document.getElementById("tryon-file").click()} data-testid="tryon-upload-btn">
            {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <UploadCloud size={16} className="mr-2" />}
            {photo ? "Change photo" : "Upload photo"}
          </Button>
          <p className="text-xs text-slate-400 mt-2">Tip: a well-lit, front-facing photo gives the best results. Your photo is saved privately to your account.</p>
        </div>

        {/* product picker */}
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold flex items-center gap-2"><Shirt size={18} className="text-[#E63946]" /> Build your look</h3>
            <span className="text-xs text-slate-400">{selectedItems.length}/2 items</span>
          </div>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products..." className="mb-3" data-testid="tryon-search" />
          <div className="grid grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1" data-testid="tryon-product-grid">
            {filtered.map((p) => (
              <button key={p.id} onClick={() => toggleItem(p)} data-testid={`tryon-product-${p.id}`}
                className={`rounded-lg overflow-hidden border-2 text-left transition-all ${isSelected(p.id) ? "border-[#E63946] ring-2 ring-[#E63946]/30" : "border-transparent hover:border-slate-300"}`}>
                <div className="aspect-[3/4] bg-slate-100 relative">
                  <img src={p.images?.[0]} alt="" className="w-full h-full object-cover" />
                  {isSelected(p.id) && <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#E63946] text-white text-[11px] font-bold flex items-center justify-center">{selectedItems.findIndex((x) => x.id === p.id) + 1}</span>}
                </div>
                <div className="p-1.5"><div className="text-[11px] font-medium line-clamp-1">{p.name}</div><div className="text-[11px] text-slate-400">{formatPrice(p.price)}</div></div>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">Pick a single piece, or a top + bottom to preview a full outfit.</p>
        </div>

        {/* result */}
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Sparkles size={18} className="text-[#D4AF37]" /> The Look</h3>
          <div className="aspect-[3/4] rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center mb-3 text-center p-4" data-testid="tryon-result">
            {generating ? (
              <>
                <Loader2 size={34} className="text-[#E63946] animate-spin mb-3" />
                <p className="text-sm text-slate-500">Styling your look…<br />this takes about 15–20 seconds</p>
              </>
            ) : result ? (
              <img src={result} alt="try-on result" className="w-full h-full object-cover" />
            ) : (
              <p className="text-sm text-slate-400">Your try-on preview will appear here</p>
            )}
          </div>
          {selectedItems.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedItems.map((p) => (
                <div key={p.id} className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-800 rounded-full pl-1 pr-2 py-1 text-xs">
                  <img src={p.images?.[0]} alt="" className="w-6 h-7 object-cover rounded-full" />
                  <span className="max-w-[90px] line-clamp-1">{p.name}</span>
                  <button onClick={() => toggleItem(p)} data-testid={`tryon-remove-${p.id}`}><X size={13} className="text-slate-400 hover:text-red-500" /></button>
                </div>
              ))}
            </div>
          )}
          <Button onClick={runTryOn} disabled={generating || !photo || !selectedItems.length} className="w-full bg-[#E63946] hover:bg-[#d12836]" data-testid="tryon-generate">
            {generating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating…</> : result ? <><RefreshCw size={16} className="mr-2" /> Try again</> : <><Sparkles size={16} className="mr-2" /> Try it on</>}
          </Button>
          {result && (
            <>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button variant="outline" onClick={() => downloadImage(result)} data-testid="tryon-download"><Download size={16} className="mr-2" /> Download</Button>
                <Button variant="outline" onClick={() => shareImage(result)} data-testid="tryon-share"><Share2 size={16} className="mr-2" /> Share</Button>
              </div>
              <Button onClick={() => addItemsToBag(selectedItems.map((p) => p.id))} className="w-full mt-2 bg-[#1D3557] hover:bg-[#162a47]" data-testid="tryon-add-look-bag">
                <ShoppingBag size={16} className="mr-2" /> Add look to bag
              </Button>
            </>
          )}
        </div>
      </div>

      {/* My Looks gallery */}
      {looks.length > 0 && (
        <div className="mt-10" data-testid="my-looks">
          <h2 className="font-display text-xl font-bold mb-1">My Looks</h2>
          <p className="text-sm text-slate-400 mb-4">Your saved try-on looks — swipe through, share or shop them anytime.</p>
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 sm:grid sm:grid-cols-3 lg:grid-cols-5 sm:overflow-visible sm:mx-0 sm:px-0 [scrollbar-width:thin]">
            {looks.map((l) => (
              <div key={l.id} className="snap-start shrink-0 w-[62%] xs:w-[46%] sm:w-auto rounded-xl border bg-white dark:bg-slate-900 overflow-hidden" data-testid={`look-${l.id}`}>
                <div className="aspect-[3/4] bg-slate-100 cursor-pointer" onClick={() => { setResult(abs(l.image_url)); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
                  <img src={abs(l.image_url)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-2">
                  <div className="text-xs font-medium line-clamp-1">{l.product_names}</div>
                  <div className="text-xs text-slate-400 mb-2">{formatPrice(l.price_inr)}</div>
                  <Button size="sm" onClick={() => addItemsToBag(l.product_ids)} className="w-full h-8 mb-1.5 bg-[#E63946] hover:bg-[#d12836] text-xs" data-testid={`look-add-bag-${l.id}`}>
                    <ShoppingBag size={13} className="mr-1" /> Add to bag
                  </Button>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => downloadImage(abs(l.image_url))} data-testid={`look-download-${l.id}`} title="Download"><Download size={14} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => shareImage(abs(l.image_url))} data-testid={`look-share-${l.id}`} title="Share"><Share2 size={14} /></Button>
                    <a href={`/product/${l.product_ids?.[0]}`} className="ml-auto text-xs font-semibold text-[#E63946] hover:underline" data-testid={`look-shop-${l.id}`}>Shop</a>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteLook(l.id)} data-testid={`look-delete-${l.id}`} title="Delete"><Trash2 size={14} className="text-red-500" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
